/**
 * The ⌘K command set.
 *
 * §11 wants one keystroke to "navigate, create-anything, run an Assistant
 * command". All three are here. Every one is derived from the same declarations
 * the sidebar and the quick-create menu use, so the palette can never offer a
 * destination the sidebar hides or a create action the user lacks the
 * permission for.
 *
 * The Assistant commands (C12 §5.5) are commands like any other — which is
 * exactly why running one is a handler lookup rather than a branch on the id's
 * shape, and why they are **filtered on `ai.assistant.use` like every other
 * entry is filtered on its own permission**. When AI is disabled at the
 * instance `ai.Routes()` mounts nothing, nobody holds that code, and the
 * palette silently has three fewer commands rather than three that fail.
 *
 * They open the right rail on the Assistant rather than navigating: the
 * Assistant answers about what the user is looking at, so taking them away from
 * it to ask would be the one thing that makes it less useful.
 *
 * Apps get a section of their own ("Open Finance") so the palette can do
 * everything the launcher does, and every "Go to" entry for a screen the C15
 * audit flagged carries the "Not in FSD" marker as its trailing hint — the
 * palette is one of the places a flagged screen is offered, so it is one of
 * the places the flag is shown.
 */

import type { Command } from '@erpx/ui'
import { computed, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'

import { t } from '@/i18n'
import { useOverlays } from '@/composables/useOverlays'
import { useQuickCreate, QUICK_CREATE_ACTIONS } from '@/composables/useQuickCreate'
import { useRightRail } from '@/composables/useRightRail'
import { AI_PERMISSIONS } from '@/modules/ai/permissions'
import { appLandingPath, isExtraneous, navFsdOf, visibleApps, type NavFsdRef } from '@/nav/apps'
import { NAV_GROUPS, navDescription, navKeywords, navLabel, navPartLabel, visibleItems } from '@/nav/items'
import { useAuthStore } from '@/stores/auth'

export const COMMAND_PREFIX = {
  navigate: 'nav:',
  app: 'app:',
  create: 'create:',
  action: 'action:',
  assistant: 'assistant:',
} as const

/**
 * The Assistant's palette commands.
 *
 * Each is a real thing the Assistant can be asked, phrased as the question a
 * user would type — the palette is a place to start a conversation, not a menu
 * of tool names. `prompt` is seeded into the composer; the user still presses
 * send, because §12 has no path where a keystroke causes a model call the user
 * did not read first.
 */
export const ASSISTANT_COMMANDS = [
  {
    id: 'open',
    labelKey: 'ai.commands.open',
    descriptionKey: 'ai.commands.openDescription',
    promptKey: '',
  },
  {
    id: 'cash',
    labelKey: 'ai.commands.cash',
    descriptionKey: 'ai.commands.cashDescription',
    promptKey: 'ai.commands.cashPrompt',
  },
  {
    id: 'aging',
    labelKey: 'ai.commands.aging',
    descriptionKey: 'ai.commands.agingDescription',
    promptKey: 'ai.commands.agingPrompt',
  },
] as const

export interface CommandSet {
  commands: ComputedRef<Command[]>
  run: (command: Command) => void
}

export function useCommands(): CommandSet {
  const router = useRouter()
  const auth = useAuthStore()
  const rail = useRightRail()
  const quickCreate = useQuickCreate()
  const overlays = useOverlays()

  const navigation = computed<Command[]>(() =>
    visibleItems(NAV_GROUPS, auth.can).map((item) => {
      const description = navDescription(item)
      // The item's FSD identification is searchable too: a reviewer holding
      // the spec types "4.2.5" or "PRC-F05", or the FSD section's name, and
      // lands on the screen that implements it. Same `navFsdOf` resolution
      // the page-header chips use, so the palette and the header agree.
      // A recorded deviation (users, roles, profile) has no spec: it inherits
      // its section's number structurally, but must never be findable as if
      // it implemented that section — so it gets no FSD words at all.
      const ref: NavFsdRef = isExtraneous(item) ? {} : navFsdOf(item)
      const fsdWords = [
        ...(ref.fsd === undefined ? [] : [ref.fsd]),
        ...(ref.code === undefined ? [] : [ref.code]),
        ...(ref.section === undefined ? [] : [t(ref.section.labelKey)]),
      ]
      const keywords = [...(navKeywords(item) ?? []), ...fsdWords]
      return {
        id: `${COMMAND_PREFIX.navigate}${item.id}`,
        label: navLabel(item),
        group: t('palette.groups.goTo'),
        icon: 'arrowRight',
        ...(description === undefined ? {} : { description }),
        ...(keywords.length === 0 ? {} : { keywords }),
        ...(isExtraneous(item) ? { hint: t('shell.fsd.notInFsd') } : {}),
      }
    }),
  )

  /** One entry per app the launcher shows, filtered by the same rule. */
  const apps = computed<Command[]>(() =>
    visibleApps(auth.can)
      .filter(({ app }) => app.inLauncher)
      .map(({ app, part }) => ({
        id: `${COMMAND_PREFIX.app}${app.id}`,
        label: t('shell.launcher.openApp', { app: t(app.labelKey) }),
        group: t('shell.launcher.paletteGroup'),
        icon: 'columns' as const,
        description: t(app.descriptionKey),
        // The numbered part name ("2 Finance") so "part 2" style queries land.
        keywords: [t(app.labelKey), navPartLabel(part)],
      })),
  )

  /** Search terms are translated too: an English keyword list is useless in dv. */
  const words = (key: string): string[] => t(key).split(/\s+/).filter((word) => word !== '')

  const creates = computed<Command[]>(() =>
    QUICK_CREATE_ACTIONS.filter((action) => auth.can(action.permission)).map((action) => ({
      id: `${COMMAND_PREFIX.create}${action.id}`,
      label: t(action.labelKey),
      group: t('palette.groups.create'),
      icon: 'plus',
      description: t(action.descriptionKey),
      keywords: words('palette.commands.createKeywords'),
    })),
  )

  const actions = computed<Command[]>(() => [
    {
      id: `${COMMAND_PREFIX.action}insights`,
      label: t('palette.commands.insights'),
      group: t('palette.groups.actions'),
      icon: 'columns',
      keywords: words('palette.commands.insightsKeywords'),
    },
    {
      id: `${COMMAND_PREFIX.action}shortcuts`,
      label: t('palette.commands.shortcuts'),
      group: t('palette.groups.actions'),
      icon: 'command',
      hint: '?',
      keywords: words('palette.commands.shortcutsKeywords'),
    },
    {
      id: `${COMMAND_PREFIX.action}signout`,
      label: t('palette.commands.signOut'),
      group: t('palette.groups.actions'),
      icon: 'external',
      keywords: words('palette.commands.signOutKeywords'),
    },
  ])

  /**
   * Assistant commands, gated on `ai.assistant.use` exactly as the create
   * commands are gated on their own permission.
   */
  const assistant = computed<Command[]>(() => {
    if (!auth.can(AI_PERMISSIONS.assistantUse)) return []
    return ASSISTANT_COMMANDS.map((command) => ({
      id: `${COMMAND_PREFIX.assistant}${command.id}`,
      label: t(command.labelKey),
      group: t('ai.commands.group'),
      icon: 'command' as const,
      description: t(command.descriptionKey),
      keywords: words('ai.commands.keywords'),
    }))
  })

  const commands = computed<Command[]>(() => [
    ...navigation.value,
    ...apps.value,
    ...creates.value,
    ...assistant.value,
    ...actions.value,
  ])

  function run(command: Command): void {
    if (command.id.startsWith(COMMAND_PREFIX.navigate)) {
      const id = command.id.slice(COMMAND_PREFIX.navigate.length)
      const item = visibleItems(NAV_GROUPS, auth.can).find((candidate) => candidate.id === id)
      if (item) void router.push(item.to)
      return
    }

    if (command.id.startsWith(COMMAND_PREFIX.app)) {
      // Resolved against the user's visible apps again, never trusted from the
      // list, and sent to the first screen the user can actually open.
      const id = command.id.slice(COMMAND_PREFIX.app.length)
      const entry = visibleApps(auth.can).find((candidate) => candidate.app.id === id)
      if (entry) void router.push(appLandingPath(entry.part))
      return
    }

    if (command.id.startsWith(COMMAND_PREFIX.assistant)) {
      // Re-checked here rather than trusted from the list: a command id can
      // reach `run` from a stale palette, and opening the Assistant for someone
      // who may not use it would be a permission model with a hole in it.
      if (!auth.can(AI_PERMISSIONS.assistantUse)) return
      const id = command.id.slice(COMMAND_PREFIX.assistant.length)
      const declared = ASSISTANT_COMMANDS.find((candidate) => candidate.id === id)
      rail.openAssistant(
        declared === undefined || declared.promptKey === '' ? '' : t(declared.promptKey),
      )
      return
    }

    if (command.id.startsWith(COMMAND_PREFIX.create)) {
      const id = command.id.slice(COMMAND_PREFIX.create.length)
      const action = QUICK_CREATE_ACTIONS.find((candidate) => candidate.id === id)
      if (action) quickCreate.open(action.id)
      return
    }

    switch (command.id) {
      case `${COMMAND_PREFIX.action}insights`:
        rail.toggle()
        break
      case `${COMMAND_PREFIX.action}shortcuts`:
        overlays.openCheatSheet()
        break
      case `${COMMAND_PREFIX.action}signout`:
        // Navigate now, not after the revoke answers: `logout()` has already
        // ended the session locally, and a hung revoke must not keep this
        // screen up (see `UserMenu.vue`). `replace`, so the previous person's
        // URL is not one Back press away.
        void auth.logout()
        void router.replace({ name: 'login' })
        break
      default:
        break
    }
  }

  return { commands, run }
}
